import { motion } from "framer-motion";
import { Check } from "lucide-react";

import { SceneCaption } from "./SceneCaption";

/**
 * Scene 2 — the back-of-envelope estimate that's about to be proven wrong.
 *
 * Visual story: a notebook-style note appears, gets stamped "Approved",
 * then a red diagonal line draws across it — the visual punchline that
 * pays off the next caption: "she'd been burned by this exact estimate
 * before."
 */
export const ScreenAssumption = () => (
  <div className="max-w-3xl" data-testid="demo-scene-assumption">
    <SceneCaption
      eyebrow="The estimate"
      body="Maya does the back-of-envelope math."
    />

    {/* The notebook-page note */}
    <motion.div
      initial={{ opacity: 0, rotate: -3, y: 24 }}
      animate={{ opacity: 1, rotate: -1.5, y: 0 }}
      transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative mt-8 self-start"
      style={{ width: "min(360px, 100%)" }}
    >
      <div
        className="relative overflow-hidden"
        style={{
          backgroundColor: "#FAF1D9",
          boxShadow:
            "0 1px 1px rgba(0,0,0,0.04), 0 12px 28px -8px rgba(196,82,30,0.18), 0 8px 16px -12px rgba(0,0,0,0.15)",
          borderRadius: "2px",
        }}
      >
        <div
          className="absolute top-0 bottom-0 left-7"
          style={{ width: "1px", backgroundColor: "#C4521E", opacity: 0.45 }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, transparent 0, transparent 31px, #E8DFC5 31px, #E8DFC5 32px)",
            opacity: 0.55,
          }}
        />

        <div className="relative px-9 py-7">
          <div className="text-[10px] uppercase tracking-[0.20em] text-[#7A5A12]">
            Back-of-envelope
          </div>
          <h3 className="mt-4 font-serif-display text-2xl text-[#1A1814] leading-tight">
            AI draft replies
          </h3>
          <p className="mt-1 text-xs text-[#56534A] tabular-nums">
            ~1,200 drafts/day · tokens only
          </p>

          <div className="mt-6 flex items-baseline gap-1.5">
            <span className="font-serif-display text-5xl xl:text-6xl text-[#1A1814] leading-none tabular-nums">
              $300
            </span>
            <span className="text-sm text-[#56534A]">/month</span>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: -4 }}
            transition={{ duration: 0.5, delay: 2.0, type: "spring", stiffness: 240, damping: 14 }}
            className="mt-6 inline-flex items-center gap-1.5 border-2 px-3 py-1 uppercase text-[11px] font-medium"
            style={{ borderColor: "#5A8A3A", color: "#5A8A3A", letterSpacing: "0.18em" }}
          >
            Approved <Check size={12} strokeWidth={2.4} />
          </motion.div>
        </div>

        {/* Red diagonal strikethrough — the visual punchline */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.55, delay: 3.2, ease: [0.65, 0, 0.35, 1] }}
          className="absolute pointer-events-none"
          style={{
            top: "50%",
            left: "-4%",
            right: "-4%",
            height: "3px",
            backgroundColor: "#C4521E",
            transform: "rotate(-7deg)",
            transformOrigin: "left center",
            boxShadow: "0 1px 2px rgba(196,82,30,0.35)",
            borderRadius: "1px",
          }}
        />
      </div>
    </motion.div>

    {/* The italic editorial line that pays off the strikethrough — in the same caption frame */}
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 4.0, ease: [0.22, 1, 0.36, 1] }}
      className="mt-8 border border-[#DDD9CF] bg-white rounded-md px-6 py-5 xl:px-7 xl:py-6"
    >
      <p className="font-serif-display italic text-lg xl:text-xl text-[#1A1814] leading-relaxed">
        But she'd been burned by this exact estimate before. So she ran it
        through SuperHumanly first.
      </p>
    </motion.div>
  </div>
);
